import { Link, useParams } from 'react-router-dom';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { news } from '@/mocks/news';
import EmptyState from '@/components/base/EmptyState';

export default function NewsDetail() {
  const { id = '' } = useParams<{ id: string }>();
  const article = news.find((a) => a.id === id);

  useDocumentTitle(article ? `${article.title} - 影视资讯 - 蜜桃网` : '影视资讯 - 蜜桃网', {
    description: article ? article.excerpt : '蜜桃网影视资讯详情，阅读最新影视新闻与影评。',
    keywords: article ? `蜜桃网,${article.category},影视资讯,${article.title}` : '蜜桃网,影视资讯',
  });

  if (!article) {
    return (
      <div className="mx-auto w-full px-4 py-20 md:px-8">
        <EmptyState
          icon="ri-article-line"
          title="资讯不存在或已删除"
          description="你访问的资讯可能已被移除，去资讯频道看看其他内容吧。"
          actionLabel="返回资讯列表"
          onAction={() => {
            window.REACT_APP_NAVIGATE?.('/news');
          }}
        />
      </div>
    );
  }

  const related = news.filter((a) => a.id !== article.id && a.category === article.category).slice(0, 3);
  const fallbackRelated = news.filter((a) => a.id !== article.id).slice(0, 3);
  const relatedList = related.length > 0 ? related : fallbackRelated;

  return (
    <div className="mx-auto w-full px-4 py-8 md:px-8 md:py-10">
      <nav className="mb-6 flex flex-wrap items-center gap-2 text-sm text-foreground-500">
        <Link to="/" className="transition-colors hover:text-primary-700">
          首页
        </Link>
        <i className="ri-arrow-right-s-line" />
        <Link to="/news" className="transition-colors hover:text-primary-700">
          影视资讯
        </Link>
        <i className="ri-arrow-right-s-line" />
        <span className="truncate text-foreground-800">{article.category}</span>
      </nav>

      <article className="mx-auto max-w-3xl">
        <span className="inline-block rounded-md bg-accent-100 px-2.5 py-1 text-xs font-medium text-accent-800">
          {article.category}
        </span>
        <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
          {article.title}
        </h1>
        <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-foreground-500">
          <span className="flex items-center gap-1.5">
            <i className="ri-user-line" />
            {article.author}
          </span>
          <span className="h-4 w-px bg-background-300" />
          <span className="flex items-center gap-1.5">
            <i className="ri-calendar-line" />
            {article.date}
          </span>
        </div>

        <div className="mt-6 overflow-hidden rounded-lg">
          <img
            src={article.cover}
            alt={article.title}
            title={`${article.title} - 蜜桃网影视资讯`}
            className="h-auto w-full object-cover"
          />
        </div>

        <div className="mt-8 space-y-5">
          {article.content.map((paragraph, index) => (
            <p key={index} className="text-sm leading-loose text-foreground-700 md:text-base">
              {paragraph}
            </p>
          ))}
        </div>

        <div className="mt-8 rounded-lg border border-background-200/70 bg-background-100 p-5 text-sm text-foreground-600">
          <p>
            本文由蜜桃网编辑整理，内容仅供演示与学习交流。更多影视资讯与资源推荐，欢迎返回
            <Link to="/news" className="mx-1 text-primary-700 hover:text-accent-600">
              资讯频道
            </Link>
            浏览。
          </p>
        </div>
      </article>

      <section className="mx-auto mt-12 max-w-5xl">
        <h2 className="flex items-center gap-2.5 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
          <span className="h-6 w-1.5 rounded-full bg-gradient-to-b from-primary-400 to-accent-500" />
          相关资讯
        </h2>
        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
          {relatedList.map((item) => (
            <Link
              key={item.id}
              to={`/news/detail/${item.id}`}
              className="group overflow-hidden rounded-lg border border-background-200/70 bg-background-50 transition-all duration-300 hover:border-primary-300"
            >
              <div className="h-40 w-full overflow-hidden">
                <img
                  src={item.cover}
                  alt={item.title}
                  title={`${item.title} - 蜜桃网影视资讯`}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="p-4">
                <span className="text-xs font-medium text-accent-600">{item.category}</span>
                <h3 className="mt-1.5 line-clamp-2 font-heading text-sm font-semibold text-foreground-950 transition-colors group-hover:text-primary-700">
                  {item.title}
                </h3>
                <span className="mt-2 block text-xs text-foreground-400">{item.date}</span>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}